import { useEffect, useMemo, useRef } from "react";

import { ensureCustomerFilePreviews } from "../../services/customerFiles";
import { collectEnsurePreviewFileIds } from "./artworkFileUtils";

const buildPreviewEnsureSignature = (ids = []) =>
  ids
    .map((value) => String(value))
    .sort((a, b) => a.localeCompare(b))
    .join(",");

const useEnsureCustomerFilePreviews = ({
  customerId,
  entries = [],
  enabled = true,
  onFilesEnsured,
} = {}) => {
  const requestedIdsRef = useRef(new Set());
  const onFilesEnsuredRef = useRef(onFilesEnsured);

  useEffect(() => {
    onFilesEnsuredRef.current = onFilesEnsured;
  }, [onFilesEnsured]);

  useEffect(() => {
    requestedIdsRef.current = new Set();
  }, [customerId]);

  const candidateIds = useMemo(
    () => collectEnsurePreviewFileIds(entries),
    [entries]
  );
  const candidateSignature = useMemo(
    () => buildPreviewEnsureSignature(candidateIds),
    [candidateIds]
  );

  useEffect(() => {
    if (!enabled || !customerId || !candidateIds.length) return undefined;

    const idsToRequest = candidateIds.filter((id) => !requestedIdsRef.current.has(String(id)));
    if (!idsToRequest.length) return undefined;

    idsToRequest.forEach((id) => requestedIdsRef.current.add(String(id)));

    let cancelled = false;
    ensureCustomerFilePreviews(customerId, idsToRequest)
      .then((files) => {
        if (cancelled) return;
        onFilesEnsuredRef.current?.(Array.isArray(files) ? files : []);
      })
      .catch((error) => {
        console.error("Failed to ensure customer file previews", error);
      });

    return () => {
      cancelled = true;
    };
  }, [candidateIds, candidateSignature, customerId, enabled]);
};

export default useEnsureCustomerFilePreviews;
